import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Body1,
    Button,
    Heading1,
    Heading2,
    HorizontalDivider,
    Image,
    Stack,
    useDisableBackButton,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    RenewalStatus as RenewalStatuses,
} from '../../../graphql/generated/graphql';
import type { PharmacyOverride } from '../../../hooks/use-config-api';
import { getProductDescription } from '../../../util/formatting/prescriber-order/get-product-description';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Prescriber } from '../../common/Prescriber';
import { ShoppingHeader } from '../../common/shopping-header/ShoppingHeader';
import { ProviderInfo } from '../../provider/ProviderInfo';

type RenewalConfig = {
    header: string;
    description1: string;
    description2: string;
    icon?: ReactNode;
};

const getRenewalConfig = (args: {
    status: RenewalStatuses;
    t: TFunction;
    productDescription?: string | null;
    providerOverrides?: PharmacyOverride;
}): RenewalConfig | null => {
    const { status, t, productDescription, providerOverrides } = args;

    switch (status) {
        case RenewalStatuses.Denied: {
            const logo = providerOverrides && (
                <Stack align="center">
                    <Image
                        src={providerOverrides.logoUrl}
                        w={providerOverrides.logoWidth}
                        h="50"
                    />
                </Stack>
            );
            return {
                icon: logo,
                header: t('renewalStatus.denied.header'),
                description1: t('renewalStatus.denied.description1', {
                    medication: productDescription,
                }),
                description2: t('renewalStatus.denied.description2'),
            };
        }
        default:
            return null;
    }
};

export const RenewalStatus = ({
    prescriberOrder,
}: {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
}) => {
    const { t, i18n } = useTranslation();
    useDisableBackButton(true);
    const configOverride = useMedicationConfigOverride();
    const renewalDetails = prescriberOrder.renewalDetails;

    if (!renewalDetails) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const prescriber = getFragment(prescriberOrder.prescriber);
    const provider = getFragment(prescriberOrder.assignedPharmacyProvider);
    const medication = getFragment(prescriberOrder.authorizedMedication);

    if (!provider || !medication) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const ndcKey =
        medication.__typename === 'UnknownMedication' && medication.originalNdc
            ? medication.originalNdc
            : medication.id;
    const medicationOverride = configOverride.ndcInfo[ndcKey];
    const productDescription = getProductDescription(
        prescriberOrder,
        i18n,
        medicationOverride
    );
    const providerOverrides = configOverride.pharmacyOverrides[provider.id];
    const config = getRenewalConfig({
        status: renewalDetails.status,
        t,
        productDescription,
        providerOverrides,
    });
    if (!config) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }
    const { icon, header, description1, description2 } = config;

    const handleCallProvider = () => {
        if (prescriber?.phoneNumber) {
            window.location.href = `tel:${prescriber.phoneNumber}`;
        }
    };

    return (
        <Stack>
            <ShoppingHeader>
                <Stack gap="sm">
                    <Heading1 ta="center">{header}</Heading1>
                    {icon}
                </Stack>
            </ShoppingHeader>

            <Stack gap="xl">
                <Stack gap="sm">
                    <Body1>{description1}</Body1>
                    <Body1>{description2}</Body1>
                </Stack>
                {renewalDetails.status === RenewalStatuses.Denied &&
                    prescriber?.phoneNumber && (
                        <Button onClick={handleCallProvider} fullWidth>
                            {t('prescription.cancelled.modal.callProvider')}
                        </Button>
                    )}
            </Stack>

            <HorizontalDivider variant="full" />

            <Stack gap="xxl">
                <Stack gap="sm">
                    <Heading2>
                        {t('orderConfirmation.pharmacy.header')}
                    </Heading2>
                    <ProviderInfo provider={provider} />
                </Stack>

                <Prescriber prescriber={prescriber} />
            </Stack>
        </Stack>
    );
};
